import { useParams, Link } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { MovieContext } from "../context/MovieContext";
import { tmdbService } from "../services/tmdbService";
import FavoriteButton from "../components/FavoriteButton";

const MovieDetail = () => {
  const { id } = useParams();
  const { movies: localMovies } = useContext(MovieContext);
  const [movie, setMovie] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadMovie = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (id.startsWith('tmdb-')) {
          const tmdbId = id.replace('tmdb-', '');
          const tmdbMovie = await tmdbService.getMovieDetails(tmdbId);
          
          const mappedMovie = {
            id: id,
            tmdbId: tmdbMovie.id,
            title: tmdbMovie.title,
            description: tmdbMovie.overview,
            genre: tmdbMovie.genres?.map(g => g.name).slice(0, 2).join(', ') || 'Unknown',
            year: tmdbMovie.release_date ? parseInt(tmdbMovie.release_date.split('-')[0]) : 0,
            poster: tmdbService.getImageUrl(tmdbMovie.poster_path, 'w500'),
            trailer: tmdbService.extractTrailer(tmdbMovie.videos) || '',
            rating: Math.min(Math.max(tmdbMovie.vote_average / 2, 0), 5).toFixed(1),
            backdrop: tmdbService.getImageUrl(tmdbMovie.backdrop_path, 'w1280'),
            runtime: tmdbMovie.runtime,
            status: tmdbMovie.status,
            tagline: tmdbMovie.tagline,
            budget: tmdbMovie.budget,
            revenue: tmdbMovie.revenue,
            originalLanguage: tmdbMovie.original_language,
            productionCompanies: tmdbMovie.production_companies?.map(c => c.name).join(', ') || ''
          };
          
          setMovie(mappedMovie);
        } else {
          const localMovie = localMovies.find((m) => m.id === Number(id) || m.id === id);
          if (localMovie) {
            setMovie(localMovie);
          } else {
            setError("Movie not found");
          }
        }
      } catch (err) {
        console.error("Error loading movie:", err);
        setError("Failed to load movie details");
      } finally {
        setIsLoading(false);
      }
    };

    loadMovie();
  }, [id, localMovies]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg sm:text-xl text-gray-400">Loading movie details...</p>
        </div>
      </div>
    );
  }

  if (error || !movie) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-5xl sm:text-6xl mb-4">◈</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-4">{error || "Movie Not Found"}</h1>
          <p className="text-gray-400 mb-6 sm:mb-8">The movie could not be loaded.</p>
          <Link to="/movies" className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg font-medium transition-all duration-300 shadow-lg shadow-orange-500/25 hover:shadow-orange-500/40 inline-flex items-center space-x-2">
            <span>Browse Movies</span>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-16 sm:pt-20">
      {/* Hero Section - Responsive */}
      <section className="relative h-[60vh] sm:h-80 lg:h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img src={movie.backdrop || movie.poster} alt={movie.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-900/50 to-transparent"></div>
        </div>
        
        {/* Back Button - Responsive positioning */}
        <div className="relative z-10 container mx-auto px-3 sm:px-4 pt-4 sm:pt-8">
          <Link to="/movies" className="inline-flex items-center space-x-2 text-white hover:text-white transition-colors bg-green-600 hover:bg-green-500 backdrop-blur-sm px-3 py-2 rounded-lg text-sm sm:text-base border border-green-500 shadow-lg shadow-green-500/20">
            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="hidden sm:inline">Back to Movies</span>
            <span className="sm:hidden">Back</span>
          </Link>
        </div>
        
        {/* Movie Info - Responsive layout */}
        <div className="relative z-10 container mx-auto px-3 sm:px-4 h-full flex items-end pb-6 sm:pb-12">
          <div className="text-white max-w-3xl">
            {/* Badges - Wrap on mobile */}
            <div className="flex flex-wrap items-center gap-2 mb-3 sm:mb-4">
              <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">{movie.year}</span>
              <span className="bg-white/20 backdrop-blur-sm text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">{movie.genre}</span>
              {movie.runtime && (
                <span className="bg-white/20 backdrop-blur-sm text-white px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium">
                  {Math.floor(movie.runtime / 60)}h {movie.runtime % 60}m
                </span>
              )}
            </div>
            
            {/* Title - Responsive font sizes */}
            <h1 className="text-2xl sm:text-4xl lg:text-6xl font-bold mb-2 sm:mb-4 leading-tight">{movie.title}</h1>
            
            {/* Tagline - Hidden on very small screens */}
            {movie.tagline && <p className="text-base sm:text-xl text-white/80 italic mb-3 sm:mb-4 hidden sm:block">"{movie.tagline}"</p>}
            
            {/* Rating */}
            <div className="flex items-center space-x-4">
              <div className="bg-gradient-to-r from-orange-500 to-pink-500 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-lg">
                <span className="text-xl sm:text-2xl font-bold">{movie.rating}</span>
                <span className="text-xs sm:text-sm ml-1">/5</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section - Responsive Grid */}
      <section className="container mx-auto px-3 sm:px-4 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-12">
          {/* Poster Column - Full width on mobile, sticky on desktop */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-8">
              {/* Mobile: Poster smaller and centered */}
              <div className="w-48 sm:w-64 lg:w-full mx-auto lg:mx-0 relative group">
                <img src={movie.poster} alt={movie.title} className="w-full rounded-xl sm:rounded-2xl shadow-2xl" />
                {/* Favorite Button Overlay */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <FavoriteButton movie={movie} size="md" />
                </div>
              </div>
              
              {/* Action Buttons - Side by side on mobile */}
              <div className="mt-4 sm:mt-6 grid grid-cols-2 lg:grid-cols-1 gap-2 sm:gap-3">
                {movie.trailer && (
                  <a href={movie.trailer} target="_blank" rel="noopener noreferrer" className="px-6 py-3 bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white rounded-lg font-medium transition-all duration-300 shadow-lg shadow-orange-500/25 w-full text-center block py-3 sm:py-2 text-sm sm:text-base">
                    <span className="flex items-center justify-center space-x-2">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                      <span>Trailer</span>
                    </span>
                  </a>
                )}
                <Link to="/movies" className="px-6 py-3 bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white rounded-lg font-medium transition-all duration-300 border border-white/20 w-full text-center block py-3 sm:py-2 text-sm sm:text-base">Browse More</Link>
              </div>
            </div>
          </div>

          {/* Details Column */}
          <div className="lg:col-span-2">
            {/* Synopsis */}
            <div className="mb-6 sm:mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Synopsis</h2>
              <p className="text-gray-400 text-base sm:text-lg leading-relaxed">{movie.description || "No description available."}</p>
            </div>

            {/* Info Grid - 2 cols on mobile, 3 on tablet+ */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6 sm:mb-8">
              {movie.status && (
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/10">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-400 mb-1">Status</h3>
                  <p className="text-sm sm:text-lg font-semibold text-white">{movie.status}</p>
                </div>
              )}
              {movie.originalLanguage && (
                <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/10">
                  <h3 className="text-xs sm:text-sm font-medium text-gray-400 mb-1">Language</h3>
                  <p className="text-sm sm:text-lg font-semibold text-white">{movie.originalLanguage.toUpperCase()}</p>
                </div>
              )}
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/10">
                <h3 className="text-xs sm:text-sm font-medium text-gray-400 mb-1">Release Year</h3>
                <p className="text-sm sm:text-lg font-semibold text-white">{movie.year}</p>
              </div>
              <div className="bg-gray-900/50 backdrop-blur-sm rounded-lg sm:rounded-xl p-3 sm:p-4 border border-white/10">
                <h3 className="text-xs sm:text-sm font-medium text-gray-400 mb-1">Rating</h3>
                <p className="text-sm sm:text-lg font-semibold text-white">{movie.rating}/5</p>
              </div>
            </div>

            {/* Trailer - Responsive aspect ratio */}
            {movie.trailer && (
              <div className="mb-6 sm:mb-8">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Trailer</h2>
                <div className="aspect-video rounded-lg sm:rounded-xl overflow-hidden bg-black">
                  <iframe src={movie.trailer} title={`${movie.title} Trailer`} className="w-full h-full" allowFullScreen></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MovieDetail;
